// @ts-check
// File: app/src/apis/llm/finalisePlan.js
//
// The deterministic half of generation: turning a provider's raw response into
// a usable plan, and the re-prompt for the single refinement pass. Split out of
// generateWorkoutPlan.js so it can be imported without the provider SDKs (Jest
// cannot load the ESM-only @google/genai), e.g. by the generation evaluation.

import { debug } from "./debug";
import {
  normalizeUnits,
  validateAndAdjustTiming,
  enforceEmomWindows,
  hoistMisplacedBlocks,
} from "./postprocess";
import { injectTransitions } from "../../utils/transitionInjector";
import {
  coerceLegacyNodeTypes,
  flattenLeafContainers,
} from "../../utils/planNormaliser";
import { sanitiseHistoryNote } from "../../utils/trainingHistory";
import { fillMissingEffortCues } from "../../utils/effortCue";

/** @typedef {import("../../types/plan").PlanNode} PlanNode */

// Re-prompt builder for the single refinement pass: the original prompt + the
// flagged issues + the plan the model AUTHORED, asking for a targeted
// correction.
//
// Two things matter for what goes in here, because this pass doubles the cost
// of a generation that fires it:
//   - The plan is serialised COMPACT. Pretty-printing a workout tree at indent
//     2 roughly doubles its token count (measured: ~1.6k vs ~0.8k on an
//     ordinary 45-minute session) and buys the model nothing.
//   - The plan is the model's OWN tree, not our finalised one. finalisePlan
//     injects ghost transitions (23 authored nodes became 32 on that same
//     session), so handing back the processed tree pays to show the model
//     nodes it never wrote and invites it to re-author them as real rests.
export const buildRefinementPrompt = (originalPrompt, plan, issues) => {
  const issueLines = issues
    .map((i) => `- [${i.severity}] ${i.message}`)
    .join("\n");
  return `${originalPrompt}

## Revision Required
Your previous response had these problems:
${issueLines}

Here is the plan you produced:
${JSON.stringify({ workout_plan: plan })}

Fix every listed problem. Leave everything the problems do not touch as it is — but where a fix needs content the plan does not yet contain (more exercises, more sets, more rounds), ADD it: a problem you cannot solve without adding is still a problem you must solve. Output the corrected plan in the same shape: wrap in {"workout_plan": { ... }} and output ONLY valid JSON.`;
};

// Finalise a raw provider response into a workout plan: structural validation
// plus the deterministic post-processing pipeline (unit normalisation, ghost
// transition injection, provenance stamping, legacy-type coercion, timing
// scaling). Throws on structural problems so the retry loop can react. Pulled
// out of generateWorkoutPlan so the optional refinement pass can reuse it.
/**
 * @param {{ workout_plan: PlanNode, [key: string]: unknown }} planObject
 * @param {Object} userProfile  Generation profile (transitionSeconds,
 *   blockSetupSeconds, activeLocation, timeAvailable, skipLastRest, …).
 * @param {Function} [onRepair]  Called once per repair the pipeline makes.
 * @returns {{ workout_plan: PlanNode, [key: string]: unknown }}
 */
export const finalisePlan = (planObject, userProfile, onRepair) => {
  // Validate structure
  if (!planObject?.workout_plan) {
    debug.error(
      "Missing workout_plan key. Got keys:",
      Object.keys(planObject || {}),
    );
    debug.error(
      "Full parsed object:",
      JSON.stringify(planObject, null, 2).substring(0, 2000),
    );
    throw new Error("Invalid structure: Missing workout_plan");
  }

  // Basic validation
  let plan = planObject.workout_plan;
  if (!plan.id || !plan.type || !plan.children) {
    debug.error("Invalid workout structure. Plan keys:", Object.keys(plan));
    debug.error(
      "id:",
      plan.id,
      "type:",
      plan.type,
      "children:",
      !!plan.children,
    );
    throw new Error("Invalid workout structure");
  }

  // Capture the recent-history rationale (issue #9 follow-up) before the
  // transform passes below — injectTransitions returns a new tree and
  // coerceLegacyNodeTypes may rebuild the root, either of which could drop
  // an unknown top-level field. Stamped onto meta further down.
  const rawHistoryNote = plan.historyNote ?? plan.meta?.historyNote;

  // Normalize units in-place (coerce "seconds"→"s", "minutes"→"m", etc.)
  // and collapse legacy SetRest → Rest.
  normalizeUnits(plan);

  // Every Work step carries an effort cue (the schema requires one, but a
  // model can still send it empty). Fill only what is missing.
  fillMissingEffortCues(plan, userProfile, onRepair);

  // Hoist children off leaf-type nodes before anything else reads the tree.
  // Only a Group holds children; a model that nests the next step INSIDE the
  // previous one produces a plan the compiler part-discards (it descends into
  // any node with children but emits only leaves), so this runs first and every
  // pass below — EMOM windows, transition injection, set consolidation, the
  // timing scaler — sees a schema-valid shape.
  const flattened = flattenLeafContainers(plan, { onRepair });
  if (flattened !== plan) {
    planObject.workout_plan = flattened;
    plan = flattened;
  }

  // Undo a block authored INSIDE another block's rounds, which multiplies its
  // cost by the enclosing round count (a "14-Min" block inside 8 rounds bills
  // 112 minutes). Must run BEFORE the two passes that would otherwise stand
  // aside: enforceEmomWindows bails on a Group child, and the timing scaler
  // exempts protocol-labelled blocks.
  hoistMisplacedBlocks(plan, onRepair);

  // Enforce the EMOM window contract (issue #63): rounds × window = the
  // duration in the block's own label, the work is counted rather than timed,
  // and the window is a self-paced countdown carrying no Rest. Runs BEFORE the
  // transition injector — a window is self-paced, so there is no changeover to
  // inject inside it.
  enforceEmomWindows(plan);

  // Inject ghost transition nodes between adjacent different exercises.
  // LLMs no longer author transitions; the injector is the single source
  // of truth, parameterised by the user's default + active-location
  // override. Two-tier:
  //   - intra-block: short "Transition" (default 15s, hidden from summary)
  //   - inter-block (root-child boundaries): longer "Setup" (default 45s,
  //     always rendered as a chapter-break divider)
  // Idempotent (skips boundaries that already carry a Rest, and respects
  // `Rest` with value: 0 as the zero-superset escape hatch).
  const resolvedTransitionSeconds = Number.isFinite(
    Number(userProfile.transitionSeconds),
  )
    ? Math.max(0, Math.round(Number(userProfile.transitionSeconds)))
    : 15;
  const resolvedBlockSetupSeconds = Number.isFinite(
    Number(userProfile.blockSetupSeconds),
  )
    ? Math.max(0, Math.round(Number(userProfile.blockSetupSeconds)))
    : 45;
  const injected = injectTransitions(plan, {
    seconds: resolvedTransitionSeconds,
    blockSeconds: resolvedBlockSetupSeconds,
  });
  // injectTransitions returns a new tree when it modifies; rehydrate the
  // local `plan` reference so subsequent steps + the returned planObject
  // see the injected nodes.
  if (injected && injected !== plan) {
    // Replace in place on planObject.workout_plan to preserve the API
    // shape callers expect.
    planObject.workout_plan = injected;
  }
  let finalPlan = planObject.workout_plan;

  // Stamp transition provenance on the plan so saved snapshots remain
  // self-describing: the renderer can show the baked-in transition values
  // even if the user's active location has since changed.
  finalPlan.meta = {
    ...(finalPlan.meta || {}),
    // Capability flag: this plan was authored under the explicit-recovery
    // prompt. Recorded for provenance; note the structural ghost-rest pass
    // below runs regardless, so the LLM over-authoring a rest between two
    // different exercises is corrected even on "explicit" plans.
    recoveryModel: "explicit",
    transitionSeconds: resolvedTransitionSeconds,
    blockSetupSeconds: resolvedBlockSetupSeconds,
    locationId: userProfile.activeLocation?.id ?? null,
    locationName: userProfile.activeLocation?.name ?? null,
    // One-line "why this session" rationale grounded in recent history
    // (issue #9). null when the model omitted it or there was no history.
    historyNote:
      sanitiseHistoryNote(rawHistoryNote) ??
      finalPlan.meta?.historyNote ??
      null,
  };

  // Structural enforcement: demote any logistical "ghost" rest the LLM
  // authored between two different exercises into a hidden transition
  // (the injector above only adds transitions where no rest exists; this
  // converts the rests that slipped through). Runs before timing validation
  // so the scaler sees the final node shape.
  finalPlan = coerceLegacyNodeTypes(finalPlan, { onRepair });
  planObject.workout_plan = finalPlan;

  // Validate and adjust timing to match requested duration
  validateAndAdjustTiming(finalPlan, userProfile.timeAvailable, {
    skipLastRest: userProfile.skipLastRest ?? true,
    onRepair,
  });

  return planObject;
};
